import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import type { SystemMessage } from '../types/systemMessage';
import { useMe } from './MeContext';
import { apiPost } from '../lib/api';
import { MESSAGE_REGISTRY } from '../lib/messages';

interface SystemMessageContextValue {
  currentMessage: SystemMessage | null;
  showMessage: (slug: string) => void;
  dismissMessage: (markAsShown: boolean) => void;
}

const SystemMessageContext =
  createContext<SystemMessageContextValue | null>(null);

interface SystemMessageProviderProps {
  children: ReactNode;
}

export function SystemMessageProvider({
  children,
}: SystemMessageProviderProps) {
  const [, setQueue] = useState<string[]>([]);
  const [currentMessage, setCurrentMessage] =
    useState<SystemMessage | null>(null);
  const { hero, refreshHero } = useMe();

  const shownMessages = hero?.shownMessages ?? [];

  // Keep a ref so callbacks always see the live currentMessage without stale closures
  const currentMessageRef = useRef<SystemMessage | null>(
    null
  );
  currentMessageRef.current = currentMessage;

  const isShown = useCallback(
    (slug: string) => shownMessages.includes(slug),
    [shownMessages]
  );

  const advanceQueueRef = useRef<(q: string[]) => void>();

  const advanceQueue = useCallback(
    (q: string[]) => {
      if (q.length === 0) return;
      if (currentMessageRef.current) return;
      const [next, ...rest] = q;

      if (isShown(next)) {
        setQueue(prev => prev.slice(1));
        advanceQueueRef.current?.(rest);
        return;
      }

      const message = MESSAGE_REGISTRY[next];
      if (!message) {
        setQueue(prev => prev.slice(1));
        advanceQueueRef.current?.(rest);
        return;
      }

      setQueue(prev => prev.slice(1));
      setCurrentMessage(message);
      currentMessageRef.current = message;
    },
    [isShown]
  );

  advanceQueueRef.current = advanceQueue;

  const showMessage = useCallback(
    (slug: string) => {
      if (isShown(slug)) return;
      if (!MESSAGE_REGISTRY[slug]) return;

      setQueue(prev => {
        const next = [...prev, slug];
        // Use ref so multiple synchronous showMessage calls don't each trigger advance
        if (!currentMessageRef.current) {
          setTimeout(
            () => advanceQueueRef.current?.(next),
            0
          );
        }
        return next;
      });
    },
    [isShown]
  );

  const dismissMessage = useCallback(
    (markAsShown: boolean) => {
      const slug = currentMessageRef.current?.slug;
      setCurrentMessage(null);
      currentMessageRef.current = null;

      if (markAsShown && slug) {
        apiPost(
          `/api/claim?slug=${encodeURIComponent(slug)}`,
          {}
        )
          .then(() => refreshHero())
          .catch(err => {
            console.error('Failed to claim message:', err);
          });
      }

      setQueue(prev => {
        setTimeout(
          () => advanceQueueRef.current?.(prev),
          100
        );
        return prev;
      });
    },
    [refreshHero]
  );

  return (
    <SystemMessageContext.Provider
      value={{
        currentMessage,
        showMessage,
        dismissMessage,
      }}
    >
      {children}
    </SystemMessageContext.Provider>
  );
}

export function useSystemMessage(): SystemMessageContextValue {
  const context = useContext(SystemMessageContext);
  if (!context) {
    throw new Error(
      'useSystemMessage must be used within a SystemMessageProvider'
    );
  }
  return context;
}
